import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/Tabs';
import HelpIcon from '@/assets/icons/Help.svg';
import DotIcon from '@/assets/icons/Dot.svg';

import { PrimaryButton } from '@/components/Button';
import { useBilling } from './hooks/useBilling';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useRequest } from 'ahooks';
import { getInvoices, getOrganization, getOverviewMonthlyDetail, MonthlyDetailDto, ToggleCard } from '@/service/overview';
import { InvoiceList } from '../Overview';
import { CardTitle } from '@/components/ui/card';
import { getPaymentMethodDetail, GetPaymentMethodDetailResponse, PaymentMethodDto } from '@/service/billing';
import dayjs from 'dayjs';
import { cn, formatNumber } from '@/lib/utils';
import CardMasterIcon from '@/assets/icons/CardMaster.svg';
import CardAmexIcon from '@/assets/icons/CardAmex.svg';
import CardDinnerIcon from '@/assets/icons/CardDinner.svg';
import CardDiscoverIcon from '@/assets/icons/CardDiscover.svg';
import CardJcbIcon from '@/assets/icons/CardJcb.svg';
import CardUnionIcon from '@/assets/icons/CardUnion.svg';
import CardUnknownIcon from '@/assets/icons/CardUnknown.svg';
import CardVisaIcon from '@/assets/icons/CardVisa.svg';
import TrashIcon from '@/assets/icons/Trash.svg';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const Billing = () => {
  const { data: invoicesData } = useRequest(getInvoices);
  const { data: userInfo, loading: isUserInfoLoading } = useRequest(getOrganization);
  const { data: overviewData } = useRequest(getOverviewMonthlyDetail);
  const { data: payMethodDetail, refresh: refreshMethodDetail } = useRequest(getPaymentMethodDetail);

  const hasPaymentMethod = userInfo?.has_payment_method || false;

  return (
    <Tabs defaultValue="Overview" className="w-full">
      <Header />
      <div className="border-b border-jen-border-primary w-full"></div>
      <TabsContent className="pt-1 lg:pt-jen-spacing-24 " value="Overview">
        {!isUserInfoLoading && <Overview hasPaymentMethod={hasPaymentMethod} overviewData={overviewData?.data} />}
      </TabsContent>
      <TabsContent className="pt-1 lg:pt-jen-spacing-24 " value="PaymentMethods">
        {!isUserInfoLoading && (
          <PayMentMethods
            refreshMethodDetail={refreshMethodDetail}
            hasPaymentMethod={hasPaymentMethod}
            // payMethodDetail={{ exp_month: 4, exp_year: 2032, last4: '4242', brand: 'unknown', funding: 'credit' }}
            payMethodDetail={payMethodDetail}
          />
        )}
      </TabsContent>
      <TabsContent className="pt-1 lg:pt-jen-spacing-24 " value="BillingHistory">
        <InvoiceList invoices={invoicesData?.data} />
      </TabsContent>
    </Tabs>
  );
};

const Overview = ({ hasPaymentMethod, overviewData }: { hasPaymentMethod: boolean; overviewData?: MonthlyDetailDto }) => {
  const { createCard } = useBilling();

  return (
    <div className="py-8 max-w-[640px] mx-auto p-4">
      <h1 className=" text-jen-content-primary text-[20px] mb-6">Plan Overview</h1>
      <div className="p-4 flex flex-col gap-4 border border-jen-border-primary rounded-lg bg-jen-background-secondary">
        <div className=" text-jen-content-tertiary text-[16px]">{hasPaymentMethod ? 'Monthly spend' : 'Current Billing Period'}</div>
        <div
          className={cn('text-jen-content-tertiary text-[64px] leading-none', {
            'text-jen-content-primary': hasPaymentMethod,
          })}
        >
          {hasPaymentMethod ? `$${formatNumber(overviewData?.monthlySpend || 0)}` : ' $0.00'}
        </div>
        {!hasPaymentMethod && (
          <PrimaryButton onClick={createCard} className="w-[182px]">
            Add Payment Details
          </PrimaryButton>
        )}
      </div>
    </div>
  );
};

const PayMentMethods = ({
  hasPaymentMethod,
  payMethodDetail,
  refreshMethodDetail,
}: {
  hasPaymentMethod: boolean;
  payMethodDetail?: GetPaymentMethodDetailResponse;
  refreshMethodDetail?: () => void;
}) => {
  const { createCard, deleteCard } = useBilling();

  return (
    <div className="py-8 p-4 text-jen-content-primary max-w-[640px] mx-auto">
      {hasPaymentMethod ? (
        <>
          <div className="text-[20px] leading-[24px] pb-6">Payment Methods</div>
          <div className="flex flex-col gap-4">
            {payMethodDetail?.data?.map((i) => {
              return (
                <PaymentCardBox
                  key={i.id}
                  payMethodDetail={i}
                  refreshMethodDetail={refreshMethodDetail}
                  canDelete={payMethodDetail?.data?.length > 1}
                />
              );
            })}
          </div>
        </>
      ) : (
        <>
          <div className="text-[20px] leading-[24px] pb-6">Payment Methods</div>
          <div className="p-4 flex flex-col gap-3 bg-jen-background-secondary rounded-lg border border-jen-border-primary">
            <div className="flex items-center gap-3">
              <HelpIcon />
              Add payment details to get started
            </div>
            <div className="pl-7 text-jen-content-secondary">
              Jen operates on a postpaid billing system and charges developers at the end of each month.
            </div>
          </div>
        </>
      )}

      <PrimaryButton
        onClick={async () => {
          await createCard();
          refreshMethodDetail?.();
        }}
        className="mt-6"
      >
        Add Payment Details
      </PrimaryButton>
    </div>
  );
};

const Header = () => {
  return (
    <div className="px-8 py-6 flex flex-col justify-between">
      <CardTitle>Billing</CardTitle>

      <TabsList className=" relative top-[26px] bg-transparent text-jen-size-heading-02 mt-jen-spacing-12 flex justify-start">
        <TabsTrigger className="tracking-[-0.14px]  text-[14px] pb-3" value="Overview">
          Overview
        </TabsTrigger>
        <TabsTrigger className="tracking-[-0.14px]  text-[14px] pb-3" value="PaymentMethods">
          Payment Methods
        </TabsTrigger>
        <TabsTrigger className="tracking-[-0.14px]  text-[14px] pb-3" value="BillingHistory">
          Billing History
        </TabsTrigger>
      </TabsList>
    </div>
  );
};

const PaymentCardBox = ({
  refreshMethodDetail,
  payMethodDetail,
  canDelete,
}: {
  payMethodDetail?: PaymentMethodDto;
  refreshMethodDetail?: () => void;
  canDelete?: boolean;
}) => {
  const { deleteCard } = useBilling();
  const renderCardIcon = () => {
    if (payMethodDetail?.brand) {
      return (
        {
          amex: <CardAmexIcon />,
          diners: <CardDinnerIcon />,
          discover: <CardDiscoverIcon />,
          jcb: <CardJcbIcon />,
          mastercard: <CardMasterIcon />,
          unionpay: <CardUnionIcon />,
          visa: <CardVisaIcon />,
          unknown: <CardUnknownIcon />,
        }[payMethodDetail?.brand] || <CardUnknownIcon />
      );
    }
  };
  return (
    <div className="p-4 flex flex-col gap-3 bg-jen-background-secondary rounded-lg border border-jen-border-primary">
      <div className="text-[16px] text-jen-content-secondary flex justify-between">
        {payMethodDetail?.is_default ? 'Default payment' : 'payment'}
        {canDelete && (
          <div
            className="flex gap-[6px] text-jen-negative-sentiment items-center cursor-pointer hover:opacity-80"
            onClick={async () => {
              if (payMethodDetail?.id) {
                await deleteCard({ id: payMethodDetail?.id });
                refreshMethodDetail?.();
              }
            }}
          >
            <TrashIcon fillColor="currentColor" />
            Delete
          </div>
        )}
      </div>
      <div className="flex items-center gap-4">
        {renderCardIcon()}

        <div>
          <div className="text-[18px] flex gap-[10px] items-center h-6">
            <div className="flex gap-1">
              {new Array(4).fill('').map(() => (
                <DotIcon />
              ))}
            </div>
            <div className="flex gap-1">
              {new Array(4).fill('').map(() => (
                <DotIcon />
              ))}
            </div>
            <div className="flex gap-1">
              {new Array(4).fill('').map(() => (
                <DotIcon />
              ))}
            </div>

            <span className="text-[18px]">{payMethodDetail?.last4}</span>
          </div>
          <div className="text-[14px] text-jen-content-tertiary">
            Expires {dayjs(`${payMethodDetail?.exp_year}-${payMethodDetail?.exp_month}-01`).format('MM/YY')}
          </div>
        </div>
      </div>
      {!payMethodDetail?.is_default && (
        <div className="flex gap-1 items-center">
          <Checkbox
            id="terms"
            name="termsAgreed"
            // checked={formData.termsAgreed}
            onCheckedChange={async (checked) => {
              if (payMethodDetail?.id) {
                try {
                  await ToggleCard({ id: payMethodDetail?.id });
                  await refreshMethodDetail?.();
                } catch (error) {
                  toast.error('Toggle card failed');
                }
              }
            }}
          />
          <label
            htmlFor="terms"
            className="text-sm text-jen-content-tertiary leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Set as primary payment method
          </label>
        </div>
      )}
    </div>
  );
};

export default Billing;
